import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, EMAIL_CONFIRM_PATH } from '@/context/AuthContext';
import ErrorBoundary from '@/components/ErrorBoundary';

const API_URL = import.meta.env.VITE_API_URL || '';
import { PaymentProvider } from '@/context/PaymentContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

// Pages
import { AboutPage } from '@/pages/AboutPage';
import { HowItWorksPage } from '@/pages/HowItWorksPage';
import { SearchPage } from '@/pages/SearchPage';
import { MatchesPage } from '@/pages/MatchesPage';
import { PricingPage } from '@/pages/PricingPage';
import { PrivacyPolicy } from '@/pages/PrivacyPolicy';
import { TermsOfService } from '@/pages/TermsOfService';
import { SuccessPage } from '@/pages/SuccessPage';
import { CancelPage } from '@/pages/CancelPage';
import { SetPasswordPage } from '@/pages/SetPasswordPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { SignupPage } from '@/pages/SignupPage';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { AuthConfirmPage } from '@/pages/AuthConfirmPage';

// RaiseBeacon Chat imports
import RaiseBeaconChat from '@/pages/chat/ChatMain';
import NewChat from '@/pages/chat/NewChat';
import ChatConversation from '@/pages/chat/ChatConversation';


import OAuthCallback from '@/pages/OAuthCallback';
import AccountSettings from '@/pages/AccountSettings';
import { Tutorial } from '@/components/Tutorial';
import { AdminDashboard } from '@/components/AdminDashboard';
import { Shield } from 'lucide-react';

// CRM Pages
import { AnalyticsPage } from '@/pages/crm/AnalyticsPage';
import { TasksPage } from '@/pages/crm/TasksPage';
import { CRMLayout } from '@/pages/crm/CRMLayout';
import { CRMDashboard } from '@/pages/crm/CRMDashboard';
import { CRMContacts } from '@/pages/crm/CRMContacts';
import { DealsPage } from '@/pages/crm/DealsPage';
import { SettingsPage } from '@/pages/crm/SettingsPage';
import { FlowPage } from '@/pages/crm/FlowPage';

// Investor Pages
import { InvestorMatching } from '@/components/investor/InvestorMatching';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AnalyticsInit() {
  return null;
}

/**
 * Protected Route Component
 * Redirects to home if user is not authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2B7FFF]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to home, but save the intended destination
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Approved User Route Component
 * Redirects to pricing if user is not subscribed (for CRM access)
 */
function ApprovedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setHasChecked(true);
    }
  }, [isLoading]);

  if (isLoading || !hasChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2B7FFF]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Investor Only Route Component
 * Redirects to CRM dashboard if user is not an investor or admin
 */
function InvestorOnlyRoute({ children }: { children: React.ReactNode }) {
  const { token, isLoading, isAdmin } = useAuth();
  const location = useLocation();
  const [isInvestor, setIsInvestor] = useState<boolean | null>(null);

  useEffect(() => {
    const checkRole = async () => {
      // Admins can access investor routes
      if (isAdmin) {
        setIsInvestor(true);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setIsInvestor(data.role === 'investor');
        } else {
          setIsInvestor(false);
        }
      } catch (error) {
        setIsInvestor(false);
      }
    };
    if (token) checkRole();
  }, [token, isAdmin]);

  if (isLoading || isInvestor === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2B7FFF]"></div>
      </div>
    );
  }

  if (!isInvestor) {
    return <Navigate to="/crm/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Entry + public auth/billing routes */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/matches" element={<MatchesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/success" element={<SuccessPage />} />
      <Route path="/cancel" element={<CancelPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path={EMAIL_CONFIRM_PATH} element={<AuthConfirmPage />} />
      <Route path="/chat" element={<RaiseBeaconChat />}>
        <Route index element={<Navigate to="/chat/new" replace />} />
        <Route path="new" element={<NewChat />} />
        <Route path=":chatId" element={<ChatConversation />} />
      </Route>
      <Route path="/oauth/callback" element={<OAuthCallback />} />
      
      {/* Legacy CRM routes - redirect to new CRM */}
      <Route path="/crm-simple" element={<Navigate to="/crm" replace />} />
      <Route path="/crm-app" element={<Navigate to="/crm" replace />} />
      <Route path="/crm-pure" element={<Navigate to="/crm" replace />} />
      <Route path="/crm-test" element={<Navigate to="/crm" replace />} />
      <Route path="/crm-minimal" element={<Navigate to="/crm" replace />} />
      <Route path="/crm-basic" element={<Navigate to="/crm" replace />} />
      <Route path="/crm-v2" element={<Navigate to="/crm" replace />} />
      
      {/* Protected Routes */}
      <Route 
        path="/account" 
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        } 
      />
      
      {/* CRM Dashboard Routes - New Professional CRM */}
      <Route 
        path="/crm" 
        element={
          <ApprovedRoute>
            <CRMLayout />
          </ApprovedRoute>
        }
      >
        <Route index element={<Navigate to="/crm/dashboard" replace />} />
        <Route path="dashboard" element={<CRMDashboard />} />
        <Route path="contacts" element={<CRMContacts />} />
        <Route path="deals" element={<DealsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="reminders" element={<TasksPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="flow" element={<FlowPage />} />
        <Route path="discover" element={<InvestorOnlyRoute><InvestorMatching /></InvestorOnlyRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function ConditionalTutorial() {
  const location = useLocation();
  const hideTutorial =
    location.pathname.startsWith('/crm') ||
    location.pathname === '/' ||
    location.pathname === '/about' ||
    location.pathname === '/how-it-works';
  if (hideTutorial) return null;
  return <Tutorial />;
}

function GlobalAdminDashboardFab() {
  const { isAdmin, isAuthenticated } = useAuth();
  const location = useLocation();
  const [adminOpen, setAdminOpen] = useState(false);

  useEffect(() => {
    if ((location.state as { openAdminDashboard?: boolean } | null)?.openAdminDashboard && isAdmin) {
      setAdminOpen(true);
    }
  }, [location.state, isAdmin]);

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <>
      <AdminDashboard open={adminOpen} onOpenChange={setAdminOpen} />
      <button
        onClick={() => setAdminOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 z-50"
        title="Admin Dashboard"
      >
        <Shield className="w-6 h-6" />
      </button>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <PaymentProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AnalyticsInit />
              <div className="flex flex-col min-h-screen">
                <div className="flex-grow">
                  <AppRoutes />
                </div>
              </div>
              <ConditionalTutorial />
              <GlobalAdminDashboardFab />
            </BrowserRouter>
          </PaymentProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
